import type { NamespaceTuple } from '@ownyou/shared-types';
import { createMissionSpineRunId, recordMissionSpineMarker } from '../mission-spine/instrumentation';
import type { MissionReaction } from '../missions/reactions';
import type { MissionState } from '../missions/lifecycle';
import type { ChangeAlert } from '../../workers/change-detector';

export const INBOX_EVENTS_NAMESPACE = 'ownyou.inbox_events';

export type InboxEventKind =
  | 'mission_lifecycle'
  | 'mission_reaction'
  | 'change_alert'
  | 'tracking_request';

export interface InboxEvent {
  id: string;
  kind: InboxEventKind;
  createdAt: number;
  readAt?: number;
  missionId?: string;
  title?: string;
  summary?: string;
  reasonCode?: string;
  counts?: Record<string, number>;
}

export interface InboxEventStore {
  get<T>(namespace: NamespaceTuple, key: string): Promise<T | null>;
  put<T>(namespace: NamespaceTuple, key: string, value: T): Promise<void>;
  list<T>(namespace: NamespaceTuple, options?: { limit?: number; offset?: number }): Promise<{ items: T[] }>;
}

export type InboxEventAppendStore = Pick<InboxEventStore, 'put'>;

export interface AppendMissionLifecycleInboxEventInput {
  store: InboxEventAppendStore;
  userId: string;
  missionId: string;
  from: MissionState;
  to: MissionState;
  title?: string;
  createdAt?: number;
}

export interface AppendMissionReactionInboxEventInput {
  store: InboxEventAppendStore;
  userId: string;
  missionId: string;
  reaction: MissionReaction;
  title?: string;
  createdAt?: number;
}

export interface AppendChangeAlertInboxEventInput {
  store: InboxEventAppendStore;
  userId: string;
  alert: ChangeAlert;
}

export interface AppendTrackingRequestInboxEventInput {
  store: InboxEventAppendStore;
  userId: string;
  id?: string;
  missionId?: string;
  title?: string;
  reasonCode?: string;
  createdAt?: number;
}

export function inboxEventsNamespace(userId: string): NamespaceTuple {
  return [INBOX_EVENTS_NAMESPACE, userId] as NamespaceTuple;
}

export async function appendMissionLifecycleInboxEvent({
  store,
  userId,
  missionId,
  from,
  to,
  title,
  createdAt = Date.now(),
}: AppendMissionLifecycleInboxEventInput): Promise<InboxEvent> {
  return appendInboxEvent(store, userId, {
    id: createInboxEventId('mission_lifecycle', missionId, createdAt),
    kind: 'mission_lifecycle',
    missionId,
    title: bodySafeText(title),
    summary: `Mission moved to ${to.replace(/_/g, ' ')}`,
    reasonCode: `${from}_to_${to}`,
    createdAt,
  });
}

export async function appendMissionReactionInboxEvent({
  store,
  userId,
  missionId,
  reaction,
  title,
  createdAt = Date.now(),
}: AppendMissionReactionInboxEventInput): Promise<InboxEvent> {
  return appendInboxEvent(store, userId, {
    id: createInboxEventId('mission_reaction', missionId, createdAt),
    kind: 'mission_reaction',
    missionId,
    title: bodySafeText(title),
    summary: `Mission reaction: ${reaction}`,
    reasonCode: `reaction_${reaction}`,
    createdAt,
  });
}

export async function appendChangeAlertInboxEvent({
  store,
  userId,
  alert,
}: AppendChangeAlertInboxEventInput): Promise<InboxEvent> {
  return appendInboxEvent(store, userId, {
    id: createInboxEventId('change_alert', alert.missionId, alert.detectedAt),
    kind: 'change_alert',
    missionId: alert.missionId,
    summary: alert.changeType === 'price_drop' ? 'Mission price dropped' : 'Mission price increased',
    reasonCode: alert.changeType,
    counts: { significancePercent: Math.round(alert.significance * 100) },
    createdAt: alert.detectedAt,
  });
}

export async function appendTrackingRequestInboxEvent({
  store,
  userId,
  id,
  missionId,
  title,
  reasonCode = 'tracking_request_available',
  createdAt = Date.now(),
}: AppendTrackingRequestInboxEventInput): Promise<InboxEvent> {
  const eventId = id
    ? (bodySafeToken(id) ?? createInboxEventId('tracking_request', missionId ?? 'tracking', createdAt))
    : createInboxEventId('tracking_request', missionId ?? 'tracking', createdAt);
  return appendInboxEvent(store, userId, {
    id: eventId,
    kind: 'tracking_request',
    missionId: missionId ? bodySafeToken(missionId) : undefined,
    title: bodySafeText(title),
    summary: 'Tracking request available',
    reasonCode: bodySafeToken(reasonCode),
    createdAt,
  });
}

export async function listInboxEvents(
  store: Pick<InboxEventStore, 'list'>,
  userId: string
): Promise<InboxEvent[]> {
  const result = await store.list<InboxEvent>(inboxEventsNamespace(userId), { limit: 250, offset: 0 });
  return result.items
    .filter(isInboxEvent)
    .sort((a, b) => b.createdAt - a.createdAt || a.id.localeCompare(b.id));
}

export async function markInboxEventsRead(
  store: InboxEventStore,
  userId: string,
  input: { ids?: string[]; now?: () => number } = {}
): Promise<InboxEvent[]> {
  const events = await listInboxEvents(store, userId);
  const idFilter = input.ids ? new Set(input.ids) : null;
  const timestamp = input.now?.() ?? Date.now();
  const updated: InboxEvent[] = [];

  for (const event of events) {
    if (idFilter && !idFilter.has(event.id)) continue;
    if (event.readAt) continue;
    const next = { ...event, readAt: timestamp };
    await store.put(inboxEventsNamespace(userId), event.id, next);
    updated.push(next);
  }

  recordMissionSpineMarker({
    runId: createMissionSpineRunId('inbox-mark-read'),
    hop: 'inbox_mark_read',
    status: updated.length > 0 ? 'success' : 'skipped',
    namespace: INBOX_EVENTS_NAMESPACE,
    reasonCode: idFilter ? 'partial_mark_read' : 'all_mark_read',
    counts: { updatedCount: updated.length },
  });

  return updated;
}

export function unreadInboxEventCount(events: InboxEvent[]): number {
  return events.filter(event => !event.readAt).length;
}

export function recordInboxFeedReadMarker({
  eventCount,
  unreadCount,
}: {
  eventCount: number;
  unreadCount: number;
}): void {
  recordMissionSpineMarker({
    runId: createMissionSpineRunId('inbox-feed-read'),
    hop: 'inbox_feed_read',
    status: eventCount > 0 ? 'success' : 'skipped',
    namespace: INBOX_EVENTS_NAMESPACE,
    reasonCode: eventCount > 0 ? 'events_loaded' : 'empty_inbox',
    counts: { eventCount, unreadCount },
  });
}

async function appendInboxEvent(
  store: InboxEventAppendStore,
  userId: string,
  event: InboxEvent
): Promise<InboxEvent> {
  const bodySafeEvent = sanitizeInboxEvent(event);
  await store.put(inboxEventsNamespace(userId), bodySafeEvent.id, bodySafeEvent);

  recordMissionSpineMarker({
    runId: createMissionSpineRunId('inbox-event'),
    hop: 'inbox_event_append',
    status: 'success',
    namespace: INBOX_EVENTS_NAMESPACE,
    sourceId: bodySafeEvent.missionId,
    reasonCode: bodySafeEvent.kind,
    counts: { unread: bodySafeEvent.readAt ? 0 : 1 },
  });

  return bodySafeEvent;
}

function sanitizeInboxEvent(event: InboxEvent): InboxEvent {
  return {
    id: bodySafeToken(event.id) ?? createInboxEventId(event.kind, event.missionId ?? 'event', event.createdAt),
    kind: event.kind,
    createdAt: event.createdAt,
    ...(event.readAt ? { readAt: event.readAt } : {}),
    ...(event.missionId ? { missionId: bodySafeToken(event.missionId) } : {}),
    ...(event.title ? { title: bodySafeText(event.title) } : {}),
    ...(event.summary ? { summary: bodySafeText(event.summary) } : {}),
    ...(event.reasonCode ? { reasonCode: bodySafeToken(event.reasonCode) } : {}),
    ...(event.counts ? { counts: sanitizeCounts(event.counts) } : {}),
  };
}

function isInboxEvent(value: unknown): value is InboxEvent {
  if (!value || typeof value !== 'object') return false;
  const raw = value as Record<string, unknown>;
  return typeof raw.id === 'string'
    && isInboxEventKind(raw.kind)
    && typeof raw.createdAt === 'number';
}

function isInboxEventKind(value: unknown): value is InboxEventKind {
  return value === 'mission_lifecycle'
    || value === 'mission_reaction'
    || value === 'change_alert'
    || value === 'tracking_request';
}

function createInboxEventId(kind: InboxEventKind, source: string, timestamp: number): string {
  return `${kind}:${bodySafeToken(source) ?? 'event'}:${Math.trunc(timestamp)}`;
}

function bodySafeToken(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const safe = value.replace(/[^\w:./@-]+/g, '_').slice(0, 96);
  return safe || undefined;
}

function bodySafeText(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const safe = value.replace(/\s+/g, ' ').trim().slice(0, 120);
  return safe || undefined;
}

function sanitizeCounts(counts: Record<string, number>): Record<string, number> | undefined {
  const safe: Record<string, number> = {};
  for (const [key, value] of Object.entries(counts)) {
    const safeKey = bodySafeToken(key);
    if (!safeKey || !Number.isFinite(value)) continue;
    safe[safeKey] = Math.max(0, Math.trunc(value));
  }
  return Object.keys(safe).length > 0 ? safe : undefined;
}
